import { Outlet } from 'react-router-dom';
import { isNil, set, update } from 'lodash/fp';
import { useCallback, useMemo, useState } from 'react';
import { LocalPathway, PathwayIntervention, YearEndingPeriod } from '@/types';
import { Container, Frame, Content, Menu, useToast } from '@kinesis/bungle';
import { PathwayScenarioSidebar } from '@/components/scenario-sidebar';
import { PeriodMenuItem } from '@/components/period-menu-item';
import { PlaceMenuItem } from '@/components/place-menu-item';
import { ScenarioMenuItem } from '@/components/scenario-menu-item';
import {
  useGetPathwayQuery,
  usePathwaySimulationQuery,
  useSavePathwayMutation,
} from '@/api/pathways';
import { useGetPlacesQuery } from '@/api/places';
import { useGetScenariosQuery } from '@/api/scenarios';
import { useInventoryId } from '@/hooks/use-inventory-id';
import { usePathwayId } from '@/hooks/use-pathway-id';
import { pathwayDraft } from '@/data/pathways';
import { targetableSubSectors } from '@/data/interventions';
import { pathwayPath } from '@/links';
import { skipToken } from '@reduxjs/toolkit/query/react';

type UpdateLocalPathway = (f: LocalPathway) => LocalPathway;

// The open-pathway page. The legacy detail structure (interventions
// sidebar + results/editor outlet) with the disclosure's configuration
// idiom: the sidebar's config rows pick the EF-scenario, the base-period
// end month, and the place scope; every edit — config or intervention —
// re-runs the simulate query live (POST-with-draft: the draft is the
// query's cache key), and Save persists separately via the sidebar's
// save bar.
const Pathway = () => {
  const inventoryId = useInventoryId();
  const pathwayId = usePathwayId();
  const toast = useToast('globalTop');
  const { data: savedPathway } = useGetPathwayQuery(pathwayId);
  const { data: places } = useGetPlacesQuery(inventoryId);
  const { data: scenarios } = useGetScenariosQuery(inventoryId);

  const [localPathway, setLocalPathway] = useState<LocalPathway | undefined>(
    undefined,
  );
  const updatePathway = useCallback(
    (f: UpdateLocalPathway) => {
      setLocalPathway((current) =>
        isNil(current) ? f(savedPathway as LocalPathway) : f(current),
      );
    },
    [savedPathway, setLocalPathway],
  );

  const addIntervention = useCallback(
    (i: PathwayIntervention) => {
      updatePathway(update('interventions', (v) => v.concat([i])));
    },
    [updatePathway],
  );
  const updateIntervention = useCallback(
    (
      id: number | string,
      f: (x: PathwayIntervention) => PathwayIntervention,
    ) => {
      updatePathway(
        update('interventions', (is) =>
          is.map((i: PathwayIntervention) => (i.id === id ? f(i) : i)),
        ),
      );
    },
    [updatePathway],
  );
  const removeIntervention = useCallback(
    (k: string | number) => {
      updatePathway(
        update('interventions', (vs) =>
          vs.filter((v: PathwayIntervention) => v.id !== k),
        ),
      );
    },
    [updatePathway],
  );
  const onRename = useCallback(
    (v: String) => {
      updatePathway(set('name', v));
    },
    [updatePathway],
  );
  const onChangeScenario = useCallback(
    (scenario: number) => {
      updatePathway(set('scenario', scenario));
    },
    [updatePathway],
  );
  const onChangeBasePeriod = useCallback(
    (period: YearEndingPeriod) => {
      updatePathway(set('basePeriod', period));
    },
    [updatePathway],
  );
  const onChangePlace = useCallback(
    (place: number | undefined) => {
      updatePathway(set('place', place));
    },
    [updatePathway],
  );

  const onDiscard = useCallback(() => {
    setLocalPathway(undefined);
  }, [setLocalPathway]);

  const [savePathway] = useSavePathwayMutation();
  const onSave = useCallback(async () => {
    if (isNil(localPathway)) return;
    try {
      await savePathway({
        id: localPathway.id,
        ...pathwayDraft(localPathway),
      }).unwrap();
      setLocalPathway(undefined);
      toast('Pathway saved.', { variant: 'success' });
    } catch {
      toast(`We couldn't save the pathway. Please try again.`, {
        variant: 'error',
      });
    }
  }, [localPathway, savePathway, toast]);

  const pathway = localPathway ?? savedPathway;
  const isDirty = !isNil(localPathway);

  // The live evaluation of the effective pathway. The draft is the cache
  // key, so config and intervention edits re-project immediately.
  const { data: evaluation, isFetching: isEvaluating } =
    usePathwaySimulationQuery(
      pathway ? { pathwayId, draft: pathwayDraft(pathway) } : skipToken,
    );

  // Targeting selections are contextual: offer only the sub-sectors the
  // basis carries, read off the simulate response (the engine stays
  // exact-match; the four fine community sub-sectors are not expressible
  // by the intent shape, so the offer is the intersection with the
  // modelling axis). Undefined = can't narrow yet (unconfigured, still
  // loading, or an older village) — the editors fall back to the whole
  // axis.
  const subSectors = useMemo(() => {
    if (evaluation?.status !== 'configured' || !evaluation.subSectors) {
      return undefined;
    }
    const carried = evaluation.subSectors;
    return targetableSubSectors.filter((s) => carried.includes(s));
  }, [evaluation]);

  const configuration = (
    <Menu>
      {(scenarios?.length ?? 0) > 0 && pathway && (
        <ScenarioMenuItem
          scenarios={scenarios ?? []}
          value={pathway.scenario}
          onChange={onChangeScenario}
        />
      )}
      {pathway && (
        <PeriodMenuItem
          period={pathway.basePeriod}
          onChange={onChangeBasePeriod}
          description={
            "Select the ending month for the pathway's base period. " +
            'The base period is 12 months up to and including the ' +
            'selected month; interventions project from it.'
          }
        />
      )}
      {(places?.length ?? 0) > 0 && pathway && (
        <PlaceMenuItem
          places={places ?? []}
          value={pathway.place}
          onChange={onChangePlace}
        />
      )}
    </Menu>
  );

  return (
    <Container>
      <Frame sizing='fit-content' borderRight>
        <Content padding='none' background='white' height='100%' width={496}>
          {pathway && (
            <PathwayScenarioSidebar
              detailPath={pathwayPath(inventoryId, pathwayId)}
              configuration={configuration}
              scenario={pathway}
              isDirty={isDirty}
              onRename={onRename}
              onUpdateIntervention={updateIntervention}
              onDiscard={onDiscard}
              onSave={onSave}
            />
          )}
        </Content>
      </Frame>
      <Frame sizing='fill-container'>
        {pathway && (
          <Outlet
            context={{
              pathway,
              evaluation,
              isEvaluating,
              subSectors,
              addIntervention,
              removeIntervention,
              updateIntervention,
            }}
          />
        )}
      </Frame>
    </Container>
  );
};

export { Pathway };
